import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { NextUp } from "@/components/Callouts";
import { circleIndex } from "@/theory/notes";

// Enharmonic-tolerant: idx("Bb") and idx("Bb") find the same seat.
const idx = circleIndex;

export default function MusicTheory10() {
  const { lesson, course, accent } = findLesson("music-theory", "10");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT10-Sharp-Keys-Animated.mp4"
        caption="Sharp key signatures — sharps stack F C G D A E B"
        hint="≈ 34 sec · watch first"
      />
      <Chapter number="10" eyebrow="Music Theory · Chapter Ten" title="Key signatures, demystified">
        <Prose>
          <p>
            The circle of fifths has more than one job. We've already used it as a layout
            of the twelve pitches; in this chapter and the next it earns a new role —
            <em> a lookup machine for key signatures</em>. Hand it any major scale and it
            will tell you, instantly, how many sharps or flats that scale carries, and in
            what order they appear at the start of the staff.
          </p>
          <p>
            Key signatures matter the moment you read sheet music. Rather than scribbling
            an accidental in front of every F♯ or B♭ in a piece, the writer collects them
            once, at the start of every line, and lets them stand for the whole song. To
            read fluently you have to recognise those clusters at a glance — and the
            circle gives you the rule that generates them.
          </p>
          <p>
            One thing worth saying up front: do <em>not</em> let sharps and flats scare
            you. They aren't strange exceptions or warning signs — they're as easy to
            play on the piano as any white note. We pianists are lucky that way. A violinist
            has to find every accidental by ear on a fretless neck; a saxophonist learns a
            separate fingering for it; a guitarist may have to shift position for it. On the
            piano, a black key is just one more key. Nothing more.
          </p>
        </Prose>
        <MTQuote>
          I would recommend use the circle of fifths, as we are going to see now, as a tool
          to help us pretty much go through all the 12 scales and not have an issue with
          pretty much any of these major scales.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Setting up the circle" title="Two halves: sharps to the right, flats to the left">
        <Prose>
          <p>
            Draw the circle in the usual clock layout. <strong>C</strong> at twelve
            o'clock. Then walk clockwise in fifths: <strong>G, D, A, E, B, F♯</strong> at
            six o'clock. Then come back up the left side in fourths (or descending
            fifths): <strong>D♭, A♭, E♭, B♭, F</strong>, and finally back to C.
          </p>
          <p>
            Now imagine — or actually draw — a dotted line straight down the middle from
            C at twelve to F♯ at six. That diameter cuts the circle into two semicircles.
            Everything on the <strong>right</strong> side will be your <em>sharp scales</em>;
            everything on the <strong>left</strong> side will be your <em>flat scales</em>.
            That single decision organises the rest of this lesson.
          </p>
          <p>
            C and F♯ are the two ambiguous keys. C major has no sharps and no flats — so
            you can put it on either side without lying. F♯ major lives at the boundary
            and can be re-spelled as G♭ major (we'll meet that twin in the next chapter).
            For this chapter we keep them on the sharp side.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths
            size={360}
            highlights={[
              { index: idx("C"),  color: "#94a3b8", label: "0" },
              { index: idx("G"),  color: "#f59e0b", label: "1" },
              { index: idx("D"),  color: "#f59e0b", label: "2" },
              { index: idx("A"),  color: "#f59e0b", label: "3" },
              { index: idx("E"),  color: "#f59e0b", label: "4" },
              { index: idx("B"),  color: "#f59e0b", label: "5" },
              { index: idx("F#"), color: "#f59e0b", label: "6" },
            ]}
          />
        </div>
      </Chapter>

      <Chapter eyebrow="The rule" title="Each clockwise step adds exactly one sharp">
        <Prose>
          <p>
            Start at C. Zero sharps, zero flats — the simplest spelling. Walk clockwise
            one step and you arrive at <strong>G major</strong>. G major has exactly
            <strong> one sharp</strong>, and you have to mug up which one: it is
            <strong> F♯</strong>. Look at it on the staff and you'll see it sitting on the
            top line of the treble clef — the F line. That's the only sharp G major needs.
          </p>
          <p>
            Walk one more step clockwise — G to D. <strong>D major</strong> has
            <strong> two sharps</strong>. It keeps the F♯ that G already had, and adds a
            new one. The new sharp is always a fifth above the previous sharp: a fifth up
            from F♯ is <strong>C♯</strong>. So D major's signature is F♯, C♯ in that order.
          </p>
        </Prose>

        <TheoryBox label="The sharp-side rule">
          Each clockwise step around the circle adds exactly one new sharp. The new sharp
          is always a perfect fifth above the previous one — and it lands a semitone
          below the new tonic.
        </TheoryBox>

        <Prose>
          <p>
            Continue around. <strong>A major</strong>, three sharps: F♯, C♯, G♯ — a fifth
            on from C♯ is G♯. <strong>E major</strong>, four sharps: F♯, C♯, G♯, D♯.
            <strong> B major</strong>, five sharps: F♯, C♯, G♯, D♯, A♯. And finally
            <strong> F♯ major</strong>, six sharps — F♯, C♯, G♯, D♯, A♯, and a sixth one
            that surprises people on first sight: <strong>E♯</strong>. We'll deal with
            that oddity in a moment.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Walking the seven sharp keys" title="From C to F♯ in single steps">
        <Steps>
          <Step><strong>C major</strong> — 0 sharps. All white keys. The clean starting point.</Step>
          <Step><strong>G major</strong> — 1 sharp: F♯. (G is C's perfect fifth.)</Step>
          <Step><strong>D major</strong> — 2 sharps: F♯, C♯. New sharp is a fifth above F♯.</Step>
          <Step><strong>A major</strong> — 3 sharps: F♯, C♯, G♯. New sharp is a fifth above C♯.</Step>
          <Step><strong>E major</strong> — 4 sharps: F♯, C♯, G♯, D♯. New sharp is a fifth above G♯.</Step>
          <Step><strong>B major</strong> — 5 sharps: F♯, C♯, G♯, D♯, A♯. Five black keys, two whites (B and E).</Step>
          <Step><strong>F♯ major</strong> — 6 sharps: F♯, C♯, G♯, D♯, A♯, E♯. The strange one — see below.</Step>
        </Steps>

        <Prose>
          <p>
            Notice the pattern in the sharps themselves. They rise in fifths, in lockstep
            with the scales they belong to: <strong>F♯ → C♯ → G♯ → D♯ → A♯ → E♯</strong>.
            That's the <em>order of sharps</em>, and it's the same order they appear at
            the start of the staff. Once you can recite that sequence, you can write any
            sharp-key signature on demand.
          </p>
        </Prose>

        <TheoryBox label="The order of sharps · mnemonic">
          <strong>F</strong>ather <strong>C</strong>harles <strong>G</strong>oes
          <strong> D</strong>own <strong>A</strong>nd <strong>E</strong>nds
          <strong> B</strong>attle. Each word's first letter is the next sharp in line —
          F, C, G, D, A, E, B.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The E♯ in F♯ major" title="Why the sixth sharp has a strange name">
        <Prose>
          <p>
            F♯ major is where most students hit their first real puzzle. The sixth sharp,
            following the pattern, has to be a fifth above A♯. Count alphabetically: A, B,
            C, D, <strong>E</strong>. So the new sharp must have an E in its name, which
            means it must be called <strong>E♯</strong>. But there's no black key called
            E♯ — the key you actually press is plain old F.
          </p>
          <p>
            Why bother spelling it E♯ at all? Because of the seven-letter rule from the
            very first chapter: a major scale must use every letter A–G exactly once. F♯
            major already uses F (as F♯, its tonic). If we wrote the sixth degree as F
            again, we'd have F twice in the same scale and skip E entirely — chaos on the
            staff.
          </p>
          <p>
            So we call it E♯ as a way of saying, in writing: <em>"don't play E in this
            scale; play the key one semitone above E"</em>. F♯ major still has only five
            black keys; the sixth sharp is a renaming, not a new black key.
          </p>
        </Prose>

        <MTQuote>
          E♯ is another way of saying: in F♯, thou shalt not play E. In other words, there
          is no E in F♯. So you won't get confused. You won't be whacking E by mistake.
          You'll actually hit the F there.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="Why the rule works" title="The recipe enforces itself">
        <Prose>
          <p>
            Here's the deeper reason every clockwise step adds exactly one sharp. The
            major-scale recipe — whole-whole-half-whole-whole-whole-half — is fixed.
            When you start that recipe on G, the only way to make the gaps come out
            right is to raise F to F♯. Move to D and the recipe forces a second
            adjustment: C becomes C♯. Move to A and the recipe demands G♯. And so on.
          </p>
          <p>
            Each new tonic is a fifth higher around the circle, and that fifth-jump is
            exactly the distance that introduces one new sharp into the scale. The
            geometry of the circle and the arithmetic of the recipe match up perfectly.
            You're not memorising a quirk — you're seeing two sides of the same
            mathematical fact.
          </p>
        </Prose>

        <Glossary>
          <Term name="Key signature" def="The cluster of sharps or flats written at the start of every line of music. It tells you which notes to alter for the whole piece." />
          <Term name="Order of sharps" def="F♯, C♯, G♯, D♯, A♯, E♯, B♯ — each new one a fifth above the last." />
          <Term name="Diameter rule" def="Draw a line from C to F♯ on the circle. The right semicircle holds all sharp keys; the left holds all flat keys." />
          <Term name="Leading tone" def="The seventh degree of a major scale — always a semitone below the tonic. The new sharp added to a key is its leading tone." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="What to practise" title="Drill the sharps until they're automatic">
        <Prose>
          <p>
            Three practices will lock this chapter in. <strong>One:</strong> write out
            all seven sharp-side scales on paper, every letter A–G exactly once, with the
            correct sharps from the order. <strong>Two:</strong> recite the order of
            sharps both ways — F♯, C♯, G♯, D♯, A♯, E♯, B♯ forward, and B♯, E♯, A♯, D♯, G♯,
            C♯, F♯ backwards. <strong>Three:</strong> drill the scale-degree recall — for
            example, the fifth note of B major is F♯; the seventh note of D major is C♯;
            the third note of A major is C♯.
          </p>
          <p>
            Once the sharp half of the circle feels boring — once you can say "E major
            has four sharps: F♯, C♯, G♯, D♯" without hesitating — you're ready to flip to
            the flat side. That's the next chapter.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/10a-key-signatures-sharps-and-flats.png"
          caption="Jason's note: the circle of fifths with all 15 key signatures written as treble clefs — 7 sharps (clockwise to C♯ major) and 7 flats (counter-clockwise to C♭ major). The corner box shows the famous enharmonic equivalents (C♯ = D♭, F♯ = G♭, etc.) where two paths around the circle meet."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-11"
        label="Chapter 11 · Circle of Fifths - Key Signatures (Flats)"
        why="Same rule, mirror image. Walk counter-clockwise from C and each step adds one flat — and every new flat is a fourth above the previous one."
      />
    </LessonShell>
  );
}
